<template>
    <div class="rating-chart">
        <div class="toolbar">
            <div class="left">
                <div class="buttons">
                    <IconButton
                        :classNames="tableMode ? '' : 'selected'"
                        :onClick="() => (tableMode = false)"
                        v-tippy="{ content: $t('report.chartView') }"
                    >
                        <md-icon>insights</md-icon>
                    </IconButton>
                    <IconButton
                        :classNames="tableMode ? 'selected' : ''"
                        :onClick="() => (tableMode = true)"
                        v-tippy="{ content: $t('report.tableView') }"
                    >
                        <md-icon>table_view</md-icon>
                    </IconButton>
                    <IconButton :onClick="exportToExcel" v-tippy="{ content: $t('report.download') }">
                        <md-icon>download</md-icon>
                    </IconButton>
                </div>
            </div>
            <div class="right"></div>
        </div>
        <TableView :cols="getCols()" :rows="getRows()" v-if="tableMode" />
        <EChartsBar
            v-else
            :labels="getXAxisData()"
            :datasets="getChartDatasets()"
            :height="320"
            :stacked="true"
        />
        <div class="stats">
            <StatCard
                :title="$t('report.totalRatingCount')"
                :value="stats.totalRatingCount"
                :unit="$t('report.rate')"
                :description="$t('report.totalRatingCountDesc')"
            />
            <StatCard
                :title="getTitle('averageRating')"
                :value="formatStat(stats.AverageRating).value"
                :unit="formatStat(stats.AverageRating).unit"
                :description="$t('report.averageRatingDesc')"
            />
            <StatCard
                :title="getTitle('weightedAverageRating')"
                :value="formatStat(stats.WeightedAverageRating).value"
                :unit="formatStat(stats.WeightedAverageRating).unit"
                :description="$t('report.weightedAverageRatingDesc')"
            />
        </div>
        <div class="stats" v-if="stats.totalRatingCount > 0">
            <StatCard
                :key="key"
                :title="key"
                :value="stats.ratingCounts[key]"
                v-for="key of Object.keys(stats.ratingCounts)"
            />
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import moment from "moment";
import _ from "lodash";
import { expertToExcel } from "../../../utils/utils";
import TableView from "../../Pages/Chatbot/pages/reports/charts/TableView.vue";
import IconButton from "../../Pages/Settings/components/IconButton.vue";
import StatCard from "../../Pages/Chatbot/pages/reports/charts/StatCard.vue";
import EChartsBar from "../../../components/charts/EChartsBar.vue";
import { REPORT_API } from "../../../api/statistics";
import log2 from "../../../utils/log2";

const log = log2("OverallRatingChart");
export default Vue.extend({
    name: "OverallRatingChart",
    components: { TableView, IconButton, StatCard, EChartsBar },
    props: {
        filters: {
            type: Object,
            default: () => ({
                users: [],
                channels: [],
                startDate: moment().startOf("day").toISOString(),
                endDate: moment().endOf("day").toISOString(),
            }),
        },
    },
    data() {
        return {
            ratingColors: {
                noRating: "rgba(156, 163, 175, 0.7)",
                oneStar: "rgba(239, 68, 68, 0.7)",
                twoStar: "rgba(249, 115, 22, 0.7)",
                threeStar: "rgba(234, 179, 8, 0.7)",
                fourStar: "rgba(34, 197, 94, 0.7)",
                fiveStar: "rgba(59, 130, 246, 0.7)",
            },
            tableMode: false,
            loading: true,
            graphData: {
                buckets: [],
                timeSeriesType: "hour",
            },
            loadingInstance: null,
            stats: this.getStats(),
        };
    },
    methods: {
        getTitle(type) {
            const hourlyText = this.$t("report.hourly");
            if (this.getStats().isHourlyAverage) {
                return `${this.$t(`report.${type}`)} ${hourlyText}`;
            }
            return this.$t(`report.${type}`);
        },
        formatStat(value) {
            if (this.stats.totalRatingCount < 1) {
                return {
                    value: this.$t("report.noEnoughtData"),
                    unit: "",
                };
            }
            return {
                value: value,
                unit: this.$t("message.point"),
            };
        },
        fetch() {
            this.loading = true;
            this.loadingInstance.show();
            REPORT_API.ratingOverall(this.filters)
                .then((res) => {
                    log("ratingOverall data fetched", res);
                    this.graphData = res;
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        getCols() {
            return [
                {
                    title: this.$t("chatbotv2.date"),
                    dataIndex: "date",
                    key: "date",
                },
                ...[this.$t("report.notRated"), "1", "2", "3", "4", "5"].map((legend) => ({
                    title: legend,
                    dataIndex: legend,
                    key: legend,
                })),
            ];
        },
        getRows() {
            const { buckets, timeSeriesType } = this.graphData;
            return buckets.map((bucket) => {
                const rates = {
                    [this.$t("report.notRated")]: bucket.noRating,
                    "1": bucket.oneStar,
                    "2": bucket.twoStar,
                    "3": bucket.threeStar,
                    "4": bucket.fourStar,
                    "5": bucket.fiveStar,
                };
                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];
                return { date, ...rates };
            });
        },
        getStats() {
            const startDate = moment(this.filters.startDate);
            const endDate = moment(this.filters.endDate);
            const buckets = this.graphData?.buckets || [];
            const totalRatingCount = _.sumBy(buckets, (bucket) => bucket.totalRatingCount);
            const sumOfRatings = _.sumBy(
                buckets,
                (bucket) =>
                    bucket.oneStar * 1 +
                    bucket.twoStar * 2 +
                    bucket.threeStar * 3 +
                    bucket.fourStar * 4 +
                    bucket.fiveStar * 5
            );
            let AverageRating = totalRatingCount ? sumOfRatings / totalRatingCount : 0;
            AverageRating = parseFloat(AverageRating.toFixed(2));
            const WeightedAverageRating = AverageRating;

            const ratingCounts = buckets.reduce(
                (acc, bucket) => {
                    acc[this.$t("report.notRated")] += bucket.noRating;
                    acc["1"] += bucket.oneStar;
                    acc["2"] += bucket.twoStar;
                    acc["3"] += bucket.threeStar;
                    acc["4"] += bucket.fourStar;
                    acc["5"] += bucket.fiveStar;
                    return acc;
                },
                {
                    [this.$t("report.notRated")]: 0,
                    "1": 0,
                    "2": 0,
                    "3": 0,
                    "4": 0,
                    "5": 0,
                }
            );

            return {
                totalRatingCount,
                AverageRating,
                WeightedAverageRating,
                ratingCounts,
                isHourlyAverage: startDate.isSame(endDate, "day"),
            };
        },
        getXAxisData() {
            const { buckets, timeSeriesType } = this.graphData;
            return buckets.map((item) => {
                if (timeSeriesType === "day") {
                    return moment(item.date).format("DD/MM");
                } else if (timeSeriesType === "hour") {
                    return moment(item.date).format("HH:mm");
                } else if (timeSeriesType === "week") {
                    return moment(item.date).format("YYYY-WW[week]");
                } else {
                    return moment(item.date).format("MMM");
                }
            });
        },
        getChartDatasets() {
            const { buckets } = this.graphData;
            const ratingMap = [
                { key: "noRating", label: this.$t("report.notRated"), color: this.ratingColors.noRating },
                { key: "oneStar", label: "1", color: this.ratingColors.oneStar },
                { key: "twoStar", label: "2", color: this.ratingColors.twoStar },
                { key: "threeStar", label: "3", color: this.ratingColors.threeStar },
                { key: "fourStar", label: "4", color: this.ratingColors.fourStar },
                { key: "fiveStar", label: "5", color: this.ratingColors.fiveStar },
            ];

            return ratingMap.map((rating) => ({
                label: rating.label,
                data: buckets.map((bucket) => bucket[rating.key] || 0),
                backgroundColor: rating.color,
                borderColor: rating.color.replace("0.7", "1"),
                borderWidth: 1,
                borderRadius: 3,
            }));
        },
        exportToExcel() {
            const { buckets, timeSeriesType } = this.graphData;
            const data = buckets.map((bucket) => {
                const rates = bucket.items
                    ? bucket.items.reduce(
                          (acc, item) => {
                              if (!item.point) acc[this.$t("report.notRated")] += 1;
                              else if (item.point === 1) acc["1"] += 1;
                              else if (item.point === 2) acc["2"] += 1;
                              else if (item.point === 3) acc["3"] += 1;
                              else if (item.point === 4) acc["4"] += 1;
                              else if (item.point === 5) acc["5"] += 1;
                              return acc;
                          },
                          { [this.$t("report.notRated")]: 0, "1": 0, "2": 0, "3": 0, "4": 0, "5": 0 }
                      )
                    : {
                          [this.$t("report.notRated")]: bucket.noRating || 0,
                          "1": bucket.oneStar || 0,
                          "2": bucket.twoStar || 0,
                          "3": bucket.threeStar || 0,
                          "4": bucket.fourStar || 0,
                          "5": bucket.fiveStar || 0,
                      };

                const date = {
                    hour: moment(bucket.date).format("DD/MM/YYYY HH:mm"),
                    day: moment(bucket.date).format("DD/MM/YYYY"),
                    month: moment(bucket.date).format("MM/YYYY"),
                    week: moment(bucket.date).format("YYYY-WW[week]"),
                }[timeSeriesType];

                return { date, ...rates };
            });
            const stats = this.getStats();
            const excelData = [
                ...data,
                { date: this.$t("report.totalRatingCount"), ...stats.ratingCounts },
                { date: this.$t("report.averageRating"), [this.$t("report.averageRating")]: stats.AverageRating },
                { date: this.$t("report.weightedAverageRating"), [this.$t("report.weightedAverageRating")]: stats.WeightedAverageRating },
            ];
            expertToExcel(excelData, `rating_timeseries.xlsx`, "Sheet 1");
        },
    },
    mounted() {
        this.loadingInstance = this.$veLoading({
            target: this.$el,
            name: "wave",
        });
        this.fetch();
    },
    watch: {
        filters: {
            handler: function (val, oldVal) {
                this.fetch();
            },
            deep: true,
        },
        graphData: {
            handler: function (val, oldVal) {
                this.stats = this.getStats();
            },
            deep: true,
        },
        loading(val) {
            if (val) {
                this.loadingInstance.show();
            } else {
                this.loadingInstance.close();
            }
        },
    },
});
</script>

<style scoped lang="scss">
.rating-chart {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .toolbar {
        display: flex;
        justify-content: space-between;

        .left {
            display: flex;

            .buttons {
                display: flex;
                border: 1px solid var(--color-border-default);
                border-radius: 6px;
                overflow: hidden;

                .icon-button {
                    padding: 5px;
                    width: 40px;
                    height: 34px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    border-right: 1px solid var(--color-border-default);
                    border-radius: unset !important;

                    &.selected {
                        background-color: var(--color-bg-info-low-hover);

                        i {
                            color: rgb(55, 162, 255);
                        }
                    }

                    &:hover {
                        background-color: var(--color-bg-neutral-pressed);
                    }
                }

                .icon-button:last-child {
                    border-right: none;
                }
            }
        }

        .right {
            display: flex;
            justify-content: flex-end;
        }
    }

    .stats {
        display: flex;
        flex-direction: row;
        gap: 10px;
        flex-wrap: wrap;
    }
}
</style>